import React, {useState} from 'react';
import {inject, observer} from 'mobx-react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import CreatePostUI from '../view/CreatePostUI';
import PostsStore from '../../store/PostsStore';
import MyLibraryModalStore from '../../../../modules/MyLibraryModal/store/MyLibraryModalStore';
import {MyLibraryModal} from '../../../../modules/MyLibraryModal';
import {
  MEDIA_FILES_ALL,
  MEDIA_FILES_IMAGE,
  MediaFile,
} from '../../../../data/models/LibraryFile/MediaFile';
import {
  POST_MAX_ITEMS,
  POST_MAX_VIDEO_DURATION_SECONDS,
} from '../../../../data/models/Posts/Post';
import {toastConfig} from '../../../../uiToolkit/Toast/toastConfig';
import {postsText} from '../../postsText';

interface Props {
  postsStore?: PostsStore;
  myLibraryModalStore?: MyLibraryModalStore;
}

const FILE_STATUS_READY = 0;

const CreatePostContainer = inject(
  'postsStore',
  'myLibraryModalStore',
)(
  observer(({postsStore, myLibraryModalStore}: Props) => {
    const [items, setItems] = useState<MediaFile[]>([]);
    const [title, setTitle] = useState('');
    const [caption, setCaption] = useState('');
    const {organizationId} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const project = location.state as {
      projectId?: string;
      projectName?: string;
    } | null;

    const acceptFiles = (files: MediaFile[], current: MediaFile[]) => {
      const accepted: MediaFile[] = [];
      let tooLong = false;
      let notReady = false;
      let unsupported = false;
      let overflow = false;

      files.forEach(file => {
        if (current.some(t => t.id === file.id)) return;

        const isVideo = file.fileType.startsWith('video');
        const isImage = file.fileType.startsWith('image');

        if (!isVideo && !isImage) {
          unsupported = true;
          return;
        }
        if (
          isVideo &&
          (file.fileStatus !== FILE_STATUS_READY || !file.duration)
        ) {
          notReady = true;
          return;
        }
        if (isVideo && file.duration! > POST_MAX_VIDEO_DURATION_SECONDS) {
          tooLong = true;
          return;
        }
        if (current.length + accepted.length >= POST_MAX_ITEMS) {
          overflow = true;
          return;
        }
        accepted.push(file);
      });

      if (tooLong) toast.error(postsText.videoTooLong, toastConfig);
      if (notReady) toast.error(postsText.videoNotReady, toastConfig);
      if (unsupported) toast.error(postsText.unsupportedType, toastConfig);
      if (overflow) toast.error(postsText.maxItemsReached, toastConfig);

      return accepted;
    };

    const onSelectMedia = () => {
      // Opening on Images keeps documents and audio out of the first view.
      myLibraryModalStore!.setExcludeDocuments(true);
      myLibraryModalStore!.setInitialSelectedFilter(MEDIA_FILES_IMAGE);
      myLibraryModalStore!.showLibrary(
        MEDIA_FILES_ALL,
        files => {
          setItems(current => [...current, ...acceptFiles(files, current)]);
        },
        true,
      );
    };

    const onMoveItem = (index: number, direction: 1 | -1) => {
      setItems(current => {
        const target = index + direction;
        if (target < 0 || target >= current.length) return current;
        const updated = [...current];
        const [moved] = updated.splice(index, 1);
        updated.splice(target, 0, moved);
        return updated;
      });
    };

    const onRemoveItem = (fileId: string) => {
      setItems(current => current.filter(t => t.id !== fileId));
    };

    const onCreate = async () => {
      if (!organizationId) return;
      const postId = await postsStore!.createPost(organizationId, {
        title: title.trim(),
        description: caption.trim() || undefined,
        libraryMediaFileIds: items.map(t => t.id),
        projectId: project?.projectId,
      });
      if (postId) {
        navigate(
          project?.projectId
            ? `/manage/${organizationId}/projects/${project.projectId}?tab=posts`
            : `/manage/${organizationId}/posts`,
        );
      }
    };

    const onCancel = () => {
      navigate(-1);
    };

    return (
      <>
        <CreatePostUI
          items={items}
          title={title}
          caption={caption}
          isLoading={postsStore!.isEditLoading}
          onTitleChange={setTitle}
          onCaptionChange={setCaption}
          onSelectMedia={onSelectMedia}
          onMoveItem={onMoveItem}
          onRemoveItem={onRemoveItem}
          onCreate={onCreate}
          onCancel={onCancel}
          projectName={project?.projectName}
        />
        <MyLibraryModal />
      </>
    );
  }),
);

export default CreatePostContainer;
